import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FiX, FiLogOut, FiSettings, FiUser } from "react-icons/fi";
import AdminSidebarSection from "./AdminSidebarSection";
import AdminSidebarItem from "./AdminSidebarItem";
import {
  ADMIN_PANEL_HOME,
  adminSidebarSections,
  getUserDisplayName,
  getUserTypeName,
} from "./adminNavigation";
import { logout } from "../../features/auth/authActions";
import { resetStep, setFormData } from "../../features/formData/formSlice";
import "./AdminSidebar.css";

const SCROLL_STORAGE_KEY = "allhomedesire:admin-shell:sidebar-scroll-top";

const AdminSidebar = ({
  sections = adminSidebarSections,
  homePath = ADMIN_PANEL_HOME,
  footerItems,
  staticHeadings = false,
  collapsed = false,
  isMobile = false,
  drawerOpen = false,
  onCloseDrawer,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const navRef = useRef(null);

  const authState = useSelector((state) => state.auth);
  const displayName = getUserDisplayName(authState) || "User";
  const userType = getUserTypeName(authState) || "CUSTOMER";
  const isPartner =
    userType === "PARTNER" || location.pathname.startsWith("/partner");

  const profilePath = isPartner
    ? "/partner/profile"
    : location.pathname.startsWith("/admin")
    ? "/admin/profile"
    : "/customer/profile";

  // Prevent background scrolling and handle Escape key when mobile drawer is open
  useEffect(() => {
    if (isMobile && drawerOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          onCloseDrawer?.();
        }
      };

      window.addEventListener("keydown", handleKeyDown);

      return () => {
        document.body.style.overflow = originalOverflow;
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isMobile, drawerOpen, onCloseDrawer]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const navElement = navRef.current;

    if (!navElement) {
      return undefined;
    }

    const storedScroll = window.sessionStorage.getItem(SCROLL_STORAGE_KEY);

    if (storedScroll !== null) {
      navElement.scrollTop = Number(storedScroll);
    }

    return undefined;
  }, [drawerOpen, isMobile]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const navElement = navRef.current;

    if (!navElement) {
      return undefined;
    }

    const handleScroll = () => {
      window.sessionStorage.setItem(
        SCROLL_STORAGE_KEY,
        String(navElement.scrollTop)
      );
    };

    navElement.addEventListener("scroll", handleScroll, { passive: true });

    return () => navElement.removeEventListener("scroll", handleScroll);
  }, [drawerOpen, isMobile]);

  useEffect(() => {
    if (isMobile) {
      onCloseDrawer?.();
    }
  }, [isMobile, location.pathname, onCloseDrawer]);

  const handleNavigate = () => {
    if (isMobile) {
      onCloseDrawer?.();
    }
  };

  const handleLogout = () => {
    if (isMobile) {
      onCloseDrawer?.();
    }
    dispatch(resetStep());
    dispatch(setFormData({}));
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const renderPanel = (drawer = false) => (
    <div
      className={`admin-sidebar__panel${
        drawer ? " admin-sidebar__panel--drawer" : ""
      }`}
    >
      <div className="admin-sidebar__header">
        <button
          type="button"
          className="admin-sidebar__brand"
          onClick={() => {
            navigate(homePath);
            if (drawer) onCloseDrawer?.();
          }}
          title="Go to dashboard"
        >
          <div className="admin-sidebar__brand-logo-wrapper">
            <img
              src="/logo.svg"
              alt="AllHome Desire Logo"
              className="admin-sidebar__brand-logo"
            />
          </div>
        </button>

        {drawer && (
          <button
            type="button"
            className="admin-sidebar__drawer-close"
            onClick={onCloseDrawer}
            aria-label="Close navigation"
          >
            <FiX size={18} />
          </button>
        )}
      </div>

      {/* Mobile Drawer User Profile Header */}
      {drawer && (
        <div className="admin-sidebar__user-card">
          <div className="admin-sidebar__user-avatar">
            {displayName.charAt(0).toUpperCase()}
          </div>
          <div className="admin-sidebar__user-info">
            <h4 className="admin-sidebar__user-name text-truncate">
              {displayName}
            </h4>
            <span
              className={`admin-sidebar__user-badge ${
                isPartner
                  ? "admin-sidebar__user-badge--partner"
                  : "admin-sidebar__user-badge--customer"
              }`}
            >
              {isPartner ? "Partner Account" : "Customer Account"}
            </span>
          </div>
          <Link
            to={profilePath}
            className="admin-sidebar__user-profile-btn"
            onClick={handleNavigate}
            title="View Profile"
            aria-label="View Profile"
          >
            <FiUser size={15} />
          </Link>
        </div>
      )}

      <div className="admin-sidebar__nav" ref={navRef}>
        {sections.map((section) => (
          <AdminSidebarSection
            key={section.id}
            title={section.title}
            items={section.items}
            staticHeading={staticHeadings}
            collapsed={!drawer && collapsed}
            onNavigate={handleNavigate}
          />
        ))}
      </div>

      {/* Pinned footer (desktop + drawer) when the layout supplies footer items */}
      {footerItems && (
        <div className="admin-sidebar__pinned-footer">
          {footerItems.map((item) => (
            <AdminSidebarItem
              key={item.to}
              item={item}
              collapsed={!drawer && collapsed}
              onNavigate={handleNavigate}
            />
          ))}
          <button
            type="button"
            className="admin-sidebar__item admin-sidebar__item--logout"
            title={!drawer && collapsed ? "Logout" : undefined}
            onClick={handleLogout}
          >
            <span className="admin-sidebar__item-icon">
              <FiLogOut size={18} />
            </span>
            <span className="admin-sidebar__item-label">Logout</span>
          </button>
        </div>
      )}

      {/* Mobile Drawer Bottom Actions */}
      {drawer && !footerItems && (
        <div className="admin-sidebar__drawer-footer">
          <Link
            to="/my-preference"
            className="admin-sidebar__footer-item"
            onClick={handleNavigate}
          >
            <FiSettings size={16} />
            <span>Settings</span>
          </Link>
          <button
            type="button"
            className="admin-sidebar__footer-item admin-sidebar__footer-item--logout"
            onClick={handleLogout}
          >
            <FiLogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`admin-sidebar-root${
        collapsed && !isMobile ? " is-collapsed" : ""
      }`}
    >
      {!isMobile && (
        <aside className="admin-sidebar__desktop-shell">{renderPanel()}</aside>
      )}

      {isMobile && drawerOpen && (
        <>
          <button
            type="button"
            className="admin-sidebar__backdrop"
            onClick={onCloseDrawer}
            aria-label="Close navigation overlay"
          />
          <aside className="admin-sidebar__drawer is-open">
            {renderPanel(true)}
          </aside>
        </>
      )}
    </div>
  );
};

export default AdminSidebar;

